// SimulationLab.tsx
import React, { useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ChatCompletionMessageParam } from "openai/resources";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Loader2 } from "lucide-react";
import { CallDetailsForm } from "@/components/simulator/CallDetailsForm";
import { PromptsSection } from "@/components/simulator/PromptsSection";
import { EdgeCasesSection } from "@/components/simulator/EdgeCasesSection";
import { ConversationsSection } from "@/components/simulator/ConversationsSection";
import { judgeConversation, refinePrompts } from "@/lib/aiPhoneAgent";

interface SimulatedConversation {
  id: string;
  scenario: string;
  conversation: ChatCompletionMessageParam[];
}

const SimulationLab = () => {
  const [activeTab, setActiveTab] = useState("details");
  const [callDescription, setCallDescription] = useState("");
  const [callDuration, setCallDuration] = useState(5);
  const [strategyType, setStrategyType] = useState("");
  const [mainSystemPrompt, setMainSystemPrompt] = useState("");
  const [strategyPrompt, setStrategyPrompt] = useState("");
  const [edgeCases, setEdgeCases] = useState<string[]>([]);
  const [simulatedConversations, setSimulatedConversations] = useState<
    SimulatedConversation[]
  >([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState({
    refining: false,
    prompts: false,
    edgeCases: false,
    conversations: false,
  });

  const handleRefinePrompts = async () => {
    try {
      setError("");
      setLoading((prev) => ({ ...prev, refining: true }));

      const judgments = await Promise.all(
        simulatedConversations.map(async (conv) => {
          const judgment = await judgeConversation(
            conv.conversation,
            conv.scenario
          );
          return { edgeCase: conv.scenario, judgment };
        })
      );

      const { refinedMainSystemPrompt, refinedStrategyPrompt } =
        await refinePrompts(mainSystemPrompt, strategyPrompt, judgments);

      setMainSystemPrompt(refinedMainSystemPrompt);
      setStrategyPrompt(refinedStrategyPrompt);
      setSimulatedConversations([]);
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to refine prompts. Please try again.");
    } finally {
      setLoading((prev) => ({ ...prev, refining: false }));
    }
  };

  const resetSection = (section: "prompts" | "edgeCases" | "conversations") => {
    switch (section) {
      case "prompts":
        setMainSystemPrompt("");
        setStrategyPrompt("");
        break;
      case "edgeCases":
        setEdgeCases([]);
        break;
      case "conversations":
        setSimulatedConversations([]);
        break;
    }
  };

  const handleAddConversation = (scenario: string) => {
    const newConversation: SimulatedConversation = {
      id: Math.random().toString(36).substring(2, 11),
      scenario,
      conversation: [],
    };
    setSimulatedConversations((prev) => [...prev, newConversation]);
    setActiveTab("conversations");
  };

  const handleDeleteConversation = (id: string) => {
    setSimulatedConversations((prev) => prev.filter((conv) => conv.id !== id));
  };

  const handleUpdateConversation = (
    id: string,
    conversation: ChatCompletionMessageParam[]
  ) => {
    setSimulatedConversations((prev) =>
      prev.map((conv) => (conv.id === id ? { ...conv, conversation } : conv))
    );
  };

  return (
    <div className="p-4 max-w-6xl">
      <h1 className="text-2xl font-bold mb-4">Dialog Simulation Lab</h1>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-4 flex flex-col"
      >
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4 h-auto">
          <TabsTrigger value="details">Call Details</TabsTrigger>
          <TabsTrigger value="prompts" className="relative">
            Prompts
            {loading.prompts && (
              <Loader2 className="w-4 h-4 ml-2 animate-spin inline" />
            )}
          </TabsTrigger>
          <TabsTrigger value="edge-cases" className="relative">
            Edge Cases
            {loading.edgeCases && (
              <Loader2 className="w-4 h-4 ml-2 animate-spin inline" />
            )}
          </TabsTrigger>
          <TabsTrigger value="conversations" className="relative">
            Simulations
            {loading.conversations ? (
              <Loader2 className="w-4 h-4 ml-2 animate-spin inline" />
            ) : (
              simulatedConversations.length > 0 && (
                <Badge variant="secondary" className="ml-2">
                  {simulatedConversations.length}
                </Badge>
              )
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="mt-6">
          <CallDetailsForm
            callDescription={callDescription}
            setCallDescription={setCallDescription}
            callDuration={callDuration}
            setCallDuration={setCallDuration}
            strategyType={strategyType}
            setStrategyType={setStrategyType}
            onGeneratePrompts={() => setActiveTab("prompts")}
            loading={loading.refining}
          />
        </TabsContent>

        <TabsContent value="prompts" className="mt-6">
          <PromptsSection
            callDescription={callDescription}
            callDuration={callDuration}
            strategyType={strategyType}
            mainSystemPrompt={mainSystemPrompt}
            strategyPrompt={strategyPrompt}
            onPromptsGenerated={(mainPrompt, strategyPrompt) => {
              setMainSystemPrompt(mainPrompt);
              setStrategyPrompt(strategyPrompt);
              setLoading((prev) => ({ ...prev, prompts: false }));
            }}
            onMainPromptChange={setMainSystemPrompt}
            onStrategyPromptChange={setStrategyPrompt}
            onReset={() => resetSection("prompts")}
            onLoadingChange={(isLoading) =>
              setLoading((prev) => ({ ...prev, prompts: isLoading }))
            }
          />
        </TabsContent>

        <TabsContent value="edge-cases" className="mt-6">
          <EdgeCasesSection
            mainSystemPrompt={mainSystemPrompt}
            strategyPrompt={strategyPrompt}
            edgeCases={edgeCases}
            onEdgeCasesGenerated={setEdgeCases}
            onAddToSimulation={handleAddConversation}
            onReset={() => resetSection("edgeCases")}
            onLoadingChange={(isLoading) =>
              setLoading((prev) => ({ ...prev, edgeCases: isLoading }))
            }
          />
        </TabsContent>

        <TabsContent value="conversations" className="mt-6">
          <ConversationsSection
            mainSystemPrompt={mainSystemPrompt}
            strategyPrompt={strategyPrompt}
            callDescription={callDescription}
            simulatedConversations={simulatedConversations}
            onAddConversation={handleAddConversation}
            onDeleteConversation={handleDeleteConversation}
            onUpdateConversation={handleUpdateConversation}
            onReset={() => resetSection("conversations")}
            onLoadingChange={(isLoading) =>
              setLoading((prev) => ({ ...prev, conversations: isLoading }))
            }
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SimulationLab;
